import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Truck, MapPin, Plus, RefreshCw, X, Check, Trash2, AlertCircle,
  Search, Save, AlertTriangle, Info, CheckCircle, XCircle,
  ChevronRight, ChevronDown, Route, ArrowUp, ArrowDown, ArrowRightLeft,
  Wand2, GripVertical,
} from 'lucide-react';
import apiClient from '../api/client';
import DashboardMenuHeader from '../components/AlmacenMenuHeader';
import './DashboardBultos.css';
import './Pedidos.css';
import './PanelAlmacen.css';
import './DashboardInventario.css';

/* ============================================================
   TOAST
   ============================================================ */
const Toast = ({ show, type, message, onClose }) => {
  useEffect(() => {
    if (!show) return;
    const timer = setTimeout(onClose, 3500);
    return () => clearTimeout(timer);
  }, [show, onClose]);

  if (!show) return null;

  const config = {
    success: { icon: CheckCircle, class: 'toast-success' },
    error: { icon: XCircle, class: 'toast-error' },
    info: { icon: Info, class: 'toast-info' },
  };
  const { icon: Icon, class: cls } = config[type] || config.info;

  return (
    <div className={`pc-toast ${cls}`} onClick={onClose}>
      <Icon size={18} />
      <span>{message}</span>
      <button onClick={onClose}><X size={14} /></button>
    </div>
  );
};

/* ============================================================
   CONFIRM DIALOG
   ============================================================ */
const ConfirmDialog = ({ show, title, message, onConfirm, onCancel, loading }) => {
  if (!show) return null;
  return (
    <div className="pc-modal-overlay" onClick={onCancel}>
      <div className="pc-confirm-modal" onClick={e => e.stopPropagation()}>
        <div className="pc-confirm-icon"><AlertTriangle size={32} /></div>
        <h3>{title || '¿Estás seguro?'}</h3>
        <p>{message}</p>
        <div className="pc-confirm-actions">
          <button onClick={onCancel} disabled={loading}>Cancelar</button>
          <button onClick={onConfirm} className="danger" disabled={loading}>
            {loading ? 'Procesando…' : <><Check size={14} /> Confirmar</>}
          </button>
        </div>
      </div>
    </div>
  );
};
/* ============================================================
   MODAL NUEVA CIUDAD
   ============================================================ */
const ModalNuevaCiudad = ({ onClose, onCreated, onNotify }) => {
  const [form, setForm] = useState({ ciudad: '', region: '', minimo_usd: 0, porcentaje_flete: 0 });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.ciudad.trim()) { setError('Ciudad requerida'); return; }
    setSaving(true); setError('');
    try {
      const res = await apiClient.post('/almacen/ciudades', {
        ciudad: form.ciudad.trim().toUpperCase(),
        region: form.region.trim(),
        minimo_usd: Number(form.minimo_usd) || 0,
        porcentaje_flete: Number(form.porcentaje_flete) || 0,
      });
      if (res.data?.success) {
        onCreated(res.data.data);
        onNotify?.('success', `Ciudad "${res.data.data.ciudad}" creada`);
        onClose();
      } else setError(res.data?.detail || 'Error al crear');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al crear ciudad');
    } finally { setSaving(false); }
  };

  return (
    <div className="pc-modal-overlay" onClick={onClose}>
      <div className="pc-modal" onClick={e => e.stopPropagation()}>
        <div className="pc-modal-header">
          <h3>Nueva Ciudad</h3>
          <button onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit} className="pc-modal-body">
          <div className="pc-form-row">
            <label>Ciudad *</label>
            <input type="text" required autoFocus value={form.ciudad}
              onChange={e => setForm(f => ({ ...f, ciudad: e.target.value }))}
              placeholder="CARACAS" />
          </div>
          <div className="pc-form-row">
            <label>Región</label>
            <input type="text" value={form.region}
              onChange={e => setForm(f => ({ ...f, region: e.target.value }))}
              placeholder="CAPITAL" />
          </div>
          <div className="pc-form-grid">
            <div className="pc-form-row">
              <label>Ingreso mínimo por flete (USD) *</label>
              <input type="number" step="10" min="0" required
                value={form.minimo_usd}
                onChange={e => setForm(f => ({ ...f, minimo_usd: e.target.value }))}
                placeholder="1500.00" />
              <small style={{ fontSize: 11, opacity: 0.6, marginTop: 2 }}>
                Si el flete calculado no llega a este monto, la consolidación se rechaza.
              </small>
            </div>
            <div className="pc-form-row">
              <label>% Flete sobre valor de factura *</label>
              <input type="number" step="0.01" min="0" max="100" required
                value={form.porcentaje_flete}
                onChange={e => setForm(f => ({ ...f, porcentaje_flete: e.target.value }))}
                placeholder="5.50" />
            </div>
          </div>
          {error && <div className="pc-form-error"><AlertCircle size={14} /> {error}</div>}
          <div className="pc-modal-actions">
            <button type="button" onClick={onClose} disabled={saving}>Cancelar</button>
            <button type="submit" className="primary" disabled={saving}>
              {saving ? 'Guardando…' : <><Check size={14} /> Crear</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ============================================================
   MODAL NUEVO CAMION
   ============================================================ */
const ModalNuevoCamion = ({ onClose, onCreated, onNotify }) => {
  const [form, setForm] = useState({ tipo: '', capacidad_kg: 0, capacidad_m3: 0, descripcion: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.tipo.trim()) { setError('Tipo requerido'); return; }
    setSaving(true); setError('');
    try {
      const res = await apiClient.post('/almacen/camiones', {
        tipo: form.tipo.trim(),
        capacidad_kg: Number(form.capacidad_kg) || 0,
        capacidad_m3: Number(form.capacidad_m3) || 0,
        descripcion: form.descripcion.trim(),
      });
      if (res.data?.success) {
        onCreated(res.data.data);
        onNotify?.('success', `Camión "${res.data.data.tipo}" creado`);
        onClose();
      } else setError(res.data?.detail || 'Error al crear');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al crear camión');
    } finally { setSaving(false); }
  };

  return (
    <div className="pc-modal-overlay" onClick={onClose}>
      <div className="pc-modal" onClick={e => e.stopPropagation()}>
        <div className="pc-modal-header">
          <h3>Nuevo Camión</h3>
          <button onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit} className="pc-modal-body">
          <div className="pc-form-row">
            <label>Tipo *</label>
            <input type="text" required autoFocus value={form.tipo}
              onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}
              placeholder="CAMION 750" />
          </div>
          <div className="pc-form-grid">
            <div className="pc-form-row">
              <label>Capacidad KG *</label>
              <input type="number" step="0.01" min="0" required
                value={form.capacidad_kg}
                onChange={e => setForm(f => ({ ...f, capacidad_kg: e.target.value }))} />
            </div>
            <div className="pc-form-row">
              <label>Capacidad M³ *</label>
              <input type="number" step="0.01" min="0" required
                value={form.capacidad_m3}
                onChange={e => setForm(f => ({ ...f, capacidad_m3: e.target.value }))} />
            </div>
          </div>
          <div className="pc-form-row">
            <label>Descripción</label>
            <input type="text" value={form.descripcion}
              onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))}
              placeholder="Camión mediano ruta regional" />
          </div>
          {error && <div className="pc-form-error"><AlertCircle size={14} /> {error}</div>}
          <div className="pc-modal-actions">
            <button type="button" onClick={onClose} disabled={saving}>Cancelar</button>
            <button type="submit" className="primary" disabled={saving}>
              {saving ? 'Guardando…' : <><Check size={14} /> Crear</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ============================================================
   TABLA CAMIONES
   ============================================================ */
const TablaCamiones = ({ camiones, setCamiones, onNuevo, onNotify, onConfirm }) => {
  const [editando, setEditando] = useState({});
  const [guardando, setGuardando] = useState({});
  const [error, setError] = useState('');
  const [filtro, setFiltro] = useState('');

  const filtrados = useMemo(() => {
    const q = filtro.toLowerCase().trim();
    if (!q) return camiones;
    return camiones.filter(c =>
      String(c.tipo || '').toLowerCase().includes(q) ||
      String(c.descripcion || '').toLowerCase().includes(q)
    );
  }, [camiones, filtro]);

  const handleChange = (id, field, value) => {
    setEditando(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
  };

  const guardar = async (id) => {
    const cambios = editando[id];
    if (!cambios || Object.keys(cambios).length === 0) return;
    if (guardando[id]) return;

    setGuardando(prev => ({ ...prev, [id]: true }));
    setError('');
    try {
      const payload = {};
      if (cambios.tipo !== undefined) payload.tipo = cambios.tipo;
      if (cambios.capacidad_kg !== undefined) payload.capacidad_kg = Number(cambios.capacidad_kg);
      if (cambios.capacidad_m3 !== undefined) payload.capacidad_m3 = Number(cambios.capacidad_m3);
      if (cambios.descripcion !== undefined) payload.descripcion = cambios.descripcion;

      const res = await apiClient.put(`/almacen/camiones/${id}`, payload);
      if (res.data?.success) {
        setCamiones(prev => prev.map(c => c.id === id ? res.data.data : c));
        setEditando(prev => { const n = { ...prev }; delete n[id]; return n; });
        onNotify?.('success', `Camión "${res.data.data.tipo}" actualizado`);
      } else {
        setError(res.data?.detail || 'Error al guardar');
        onNotify?.('error', res.data?.detail || 'Error al guardar');
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Error al guardar camión';
      setError(msg);
      onNotify?.('error', msg);
    } finally {
      setGuardando(prev => ({ ...prev, [id]: false }));
    }
  };

  const guardarTodos = async () => {
    const ids = Object.keys(editando).filter(id => editando[id] && Object.keys(editando[id]).length > 0);
    if (ids.length === 0) return;
    for (const id of ids) await guardar(Number(id));
  };

  const descartar = (id) => {
    setEditando(prev => { const n = { ...prev }; delete n[id]; return n; });
  };

  const eliminar = (id, tipo) => {
    onConfirm?.(
      'Deshabilitar camión',
      `¿Deshabilitar el camión "${tipo}"? No aparecerá en el simulador de consolidación.`,
      async () => {
        try {
          const res = await apiClient.delete(`/almacen/camiones/${id}`);
          if (res.data?.success) {
            setCamiones(prev => prev.filter(c => c.id !== id));
            onNotify?.('success', `Camión "${tipo}" deshabilitado`);
          }
        } catch (err) {
          onNotify?.('error', err.response?.data?.detail || 'Error al eliminar');
        }
      }
    );
  };

  const valor = (camion, field) => {
    const e = editando[camion.id];
    return e && e[field] !== undefined ? e[field] : (camion[field] ?? '');
  };

  const tieneCambios = (id) => editando[id] && Object.keys(editando[id]).length > 0;
  const totalCambios = Object.keys(editando).filter(id => tieneCambios(Number(id))).length;

  return (
    <div className="pc-table-card">
      <div className="pc-table-header">
        <div className="pc-table-title">
          <Truck size={16} />
          <h3>Camiones</h3>
          <span className="pc-count">{camiones.length}</span>
          {totalCambios > 0 && <span className="pc-count-pending">{totalCambios} sin guardar</span>}
        </div>
        <div className="pc-table-actions">
          {totalCambios > 0 && (
            <button className="pc-btn-save-all" onClick={guardarTodos}>
              <Save size={14} /> Guardar cambios ({totalCambios})
            </button>
          )}
          <button className="pc-btn-add" onClick={onNuevo}>
            <Plus size={14} /> Agregar
          </button>
        </div>
      </div>

      <div className="pc-search-bar">
        <Search size={14} className="pc-search-icon" />
        <input
          type="text"
          placeholder="Filtrar por tipo o descripción…"
          value={filtro}
          onChange={e => setFiltro(e.target.value)}
        />
        {filtro && (
          <button className="pc-search-clear-btn" onClick={() => setFiltro('')} title="Limpiar filtro">
            <X size={13} />
          </button>
        )}
        {filtro && (
          <span className="pc-search-count-badge">
            {filtrados.length} de {camiones.length}
          </span>
        )}
      </div>

      {error && <div className="pc-inline-error"><AlertCircle size={13} /> {error}</div>}

      <div className="pc-table-scroll">
        <table className="pc-table">
          <thead>
            <tr>
              <th style={{ width: 130 }}>Tipo</th>
              <th style={{ width: 90 }}>Cap. KG</th>
              <th style={{ width: 90 }}>Cap. M³</th>
              <th>Descripción</th>
              <th style={{ width: 140 }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {camiones.length === 0 ? (
              <tr><td colSpan={5} className="pc-empty">Sin camiones cargados</td></tr>
            ) : camiones.map(c => {
              const cambiado = tieneCambios(c.id);
              return (
                <tr key={c.id} className={cambiado ? 'has-changes' : ''}>
                  <td><input className="pc-input" value={valor(c, 'tipo')}
                    onChange={e => handleChange(c.id, 'tipo', e.target.value)} /></td>
                  <td><input className="pc-input num" type="number" step="0.01"
                    value={valor(c, 'capacidad_kg')}
                    onChange={e => handleChange(c.id, 'capacidad_kg', e.target.value)} /></td>
                  <td><input className="pc-input num" type="number" step="0.01"
                    value={valor(c, 'capacidad_m3')}
                    onChange={e => handleChange(c.id, 'capacidad_m3', e.target.value)} /></td>
                  <td><input className="pc-input" value={valor(c, 'descripcion')}
                    onChange={e => handleChange(c.id, 'descripcion', e.target.value)} /></td>
                  <td className="pc-actions-cell">
                    {guardando[c.id] ? (
                      <span className="pc-saving">Guardando…</span>
                    ) : cambiado ? (
                      <div className="pc-row-actions">
                        <button className="pc-btn-save-row" onClick={() => guardar(c.id)}>
                          <Save size={13} /> Guardar
                        </button>
                        <button className="pc-btn-cancel-row" onClick={() => descartar(c.id)}>
                          <X size={13} />
                        </button>
                      </div>
                    ) : (
                      <button className="pc-btn-icon danger" onClick={() => eliminar(c.id, c.tipo)}>
                        <Trash2 size={13} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ============================================================
   TABLA CIUDADES
   ============================================================ */
const TablaCiudades = ({ ciudades, setCiudades, onNuevo, onSync, onNotify, onConfirm }) => {
  const [editando, setEditando] = useState({});
  const [guardando, setGuardando] = useState({});
  const [error, setError] = useState('');
  const [sincronizando, setSincronizando] = useState(false);
  const [filtro, setFiltro] = useState('');

  const filtradas = useMemo(() => {
    const q = filtro.toLowerCase().trim();
    if (!q) return ciudades;
    return ciudades.filter(c =>
      String(c.ciudad || '').toLowerCase().includes(q) ||
      String(c.region || '').toLowerCase().includes(q)
    );
  }, [ciudades, filtro]);

  const handleChange = (id, field, value) => {
    setEditando(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
  };

  const guardar = async (id) => {
    const cambios = editando[id];
    if (!cambios || Object.keys(cambios).length === 0) return;
    if (guardando[id]) return;

    setGuardando(prev => ({ ...prev, [id]: true }));
    setError('');
    try {
      const payload = {};
      ['ciudad', 'region', 'minimo_usd', 'porcentaje_flete'].forEach(k => {
        if (cambios[k] !== undefined) {
          payload[k] = (k === 'minimo_usd' || k === 'porcentaje_flete')
            ? Number(cambios[k])
            : cambios[k];
        }
      });

      const res = await apiClient.put(`/almacen/ciudades/${id}`, payload);
      if (res.data?.success) {
        setCiudades(prev => prev.map(c => c.id === id ? res.data.data : c));
        setEditando(prev => { const n = { ...prev }; delete n[id]; return n; });
        onNotify?.('success', `Ciudad "${res.data.data.ciudad}" actualizada`);
      } else {
        setError(res.data?.detail || 'Error al guardar');
        onNotify?.('error', res.data?.detail || 'Error al guardar');
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Error al guardar ciudad';
      setError(msg);
      onNotify?.('error', msg);
    } finally {
      setGuardando(prev => ({ ...prev, [id]: false }));
    }
  };

  const guardarTodos = async () => {
    const ids = Object.keys(editando).filter(id => editando[id] && Object.keys(editando[id]).length > 0);
    if (ids.length === 0) return;
    for (const id of ids) await guardar(Number(id));
  };

  const descartar = (id) => {
    setEditando(prev => { const n = { ...prev }; delete n[id]; return n; });
  };

  const eliminar = (id, ciudad) => {
    onConfirm?.(
      'Deshabilitar ciudad',
      `¿Deshabilitar la ciudad "${ciudad}"? No aparecerá en el tarifario de consolidación.`,
      async () => {
        try {
          const res = await apiClient.delete(`/almacen/ciudades/${id}`);
          if (res.data?.success) {
            setCiudades(prev => prev.filter(c => c.id !== id));
            onNotify?.('success', `Ciudad "${ciudad}" deshabilitada`);
          }
        } catch (err) {
          onNotify?.('error', err.response?.data?.detail || 'Error al eliminar');
        }
      }
    );
  };

  const sync = async () => {
    setSincronizando(true);
    try {
      const res = await apiClient.post('/almacen/ciudades/sync');
      if (res.data?.success) {
        await onSync();
        const d = res.data.data;
        onNotify?.('success', `${d.nuevas_desde_maestra + d.nuevas_desde_notas} nuevas sincronizadas.`);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Error al sincronizar';
      setError(msg);
      onNotify?.('error', msg);
    } finally {
      setSincronizando(false);
    }
  };

  const valor = (ciudad, field) => {
    const e = editando[ciudad.id];
    return e && e[field] !== undefined ? e[field] : (ciudad[field] ?? '');
  };

  const tieneCambios = (id) => editando[id] && Object.keys(editando[id]).length > 0;
  const totalCambios = Object.keys(editando).filter(id => tieneCambios(Number(id))).length;

  return (
    <div className="pc-table-card">
      <div className="pc-table-header">
        <div className="pc-table-title">
          <MapPin size={16} />
          <h3>Ciudades · Tarifario</h3>
          <span className="pc-count">{ciudades.length}</span>
          {totalCambios > 0 && <span className="pc-count-pending">{totalCambios} sin guardar</span>}
        </div>
        <div className="pc-table-actions">
          {totalCambios > 0 && (
            <button className="pc-btn-save-all" onClick={guardarTodos}>
              <Save size={14} /> Guardar ({totalCambios})
            </button>
          )}
          <button className="pc-btn-sync" onClick={sync} disabled={sincronizando} title="Sincronizar desde notas y clientes">
            <RefreshCw size={13} className={sincronizando ? 'spin-anim' : ''} />
          </button>
          <button className="pc-btn-add" onClick={onNuevo}>
            <Plus size={14} /> Agregar
          </button>
        </div>
      </div>

      <div className="pc-search-bar">
        <Search size={14} className="pc-search-icon" />
        <input
          type="text"
          placeholder="Filtrar por ciudad o región…"
          value={filtro}
          onChange={e => setFiltro(e.target.value)}
        />
        {filtro && (
          <button className="pc-search-clear-btn" onClick={() => setFiltro('')} title="Limpiar filtro">
            <X size={13} />
          </button>
        )}
        {filtro && (
          <span className="pc-search-count-badge">
            {filtradas.length} de {ciudades.length}
          </span>
        )}
      </div>

      {error && <div className="pc-inline-error"><AlertCircle size={13} /> {error}</div>}

      <div className="pc-table-scroll">
        <table className="pc-table pc-table-tarifario">
          <thead>
            <tr>
              <th style={{ minWidth: 140 }}>Ciudad</th>
              <th style={{ minWidth: 160 }} title="El flete calculado debe alcanzar este monto para poder consolidar">
                Mín. Flete USD
              </th>
              <th style={{ minWidth: 160 }} title="Porcentaje del valor de factura que se cobra como flete">
                % Flete
              </th>
              <th style={{ minWidth: 120 }}>Región</th>
              <th style={{ width: 140 }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filtradas.length === 0 ? (
              <tr><td colSpan={5} className="pc-empty">
                {ciudades.length === 0 ? 'Sin ciudades. Usa "Agregar" o el botón sync.' : 'Sin coincidencias'}
              </td></tr>
            ) : filtradas.map(c => {
              const cambiado = tieneCambios(c.id);
              return (
                <tr key={c.id} className={cambiado ? 'has-changes' : ''}>
                  <td>
                    <input className="pc-input" value={valor(c, 'ciudad')}
                      onChange={e => handleChange(c.id, 'ciudad', e.target.value)} />
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span className="pc-money-prefix">$</span>
                      <input className="pc-input num" type="number" step="10" min="0"
                        value={valor(c, 'minimo_usd')}
                        placeholder="0.00"
                        onChange={e => handleChange(c.id, 'minimo_usd', e.target.value)} />
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <input className="pc-input num" type="number" step="0.01" min="0" max="100"
                        value={valor(c, 'porcentaje_flete')}
                        placeholder="0.00"
                        onChange={e => handleChange(c.id, 'porcentaje_flete', e.target.value)} />
                      <span className="pc-money-suffix">%</span>
                    </div>
                  </td>
                  <td>
                    <input className="pc-input" value={valor(c, 'region')}
                      onChange={e => handleChange(c.id, 'region', e.target.value)} />
                  </td>
                  <td className="pc-actions-cell">
                    {guardando[c.id] ? (
                      <span className="pc-saving">Guardando…</span>
                    ) : cambiado ? (
                      <div className="pc-row-actions">
                        <button className="pc-btn-save-row" onClick={() => guardar(c.id)}>
                          <Save size={13} /> Guardar
                        </button>
                        <button className="pc-btn-cancel-row" onClick={() => descartar(c.id)}>
                          <X size={13} />
                        </button>
                      </div>
                    ) : (
                      <button className="pc-btn-icon danger" onClick={() => eliminar(c.id, c.ciudad)}>
                        <Trash2 size={13} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ============================================================
   MODAL MOVER CIUDADES A OTRA RUTA
   ============================================================ */
const ModalMoverCiudades = ({ origen, ciudades, rutas, onClose, onMover }) => {
  const [creandoNueva, setCreandoNueva] = useState(false);
  const [nuevaRuta, setNuevaRuta] = useState('');
  const [colorNueva, setColorNueva] = useState('#3b82f6');

  const destinos = rutas.filter(r => r.ruta !== origen);
  const [rutaDestino, setRutaDestino] = useState(destinos[0]?.ruta || '');

  const handleMover = () => {
    if (creandoNueva) {
      if (!nuevaRuta.trim()) return;
      onMover(nuevaRuta.trim().toUpperCase(), colorNueva, 99);
    } else {
      const dest = rutas.find(r => r.ruta === rutaDestino);
      if (!dest) return;
      onMover(dest.ruta, dest.color, dest.ruta_orden);
    }
  };

  return (
    <div className="pc-modal-overlay" onClick={onClose}>
      <div className="pc-modal" style={{ maxWidth: 500 }} onClick={e => e.stopPropagation()}>
        <div className="pc-modal-header">
          <h3><ArrowRightLeft size={16} /> Mover ciudades</h3>
          <button onClick={onClose}><X size={16} /></button>
        </div>
        <div className="pc-modal-body">
          <div className="pc-form-row">
            <label>Ciudades a mover ({ciudades.length})</label>
            <div className="pc-mover-ciudades-list">
              {ciudades.map(c => (
                <span key={c} className="pc-mover-ciudad-pill">{c}</span>
              ))}
            </div>
          </div>

          <div className="pc-form-row">
            <label>Ruta destino</label>
            {!creandoNueva ? (
              <>
                <select
                  value={rutaDestino}
                  onChange={e => setRutaDestino(e.target.value)}
                  className="pc-filter-select">
                  {destinos.length === 0 ? (
                    <option value="">— No hay otras rutas —</option>
                  ) : destinos.map(r => (
                    <option key={r.ruta} value={r.ruta}>
                      {r.ruta} ({r.total_ciudades} ciudades)
                    </option>
                  ))}
                </select>
                <button type="button" className="pc-btn-link"
                  onClick={() => setCreandoNueva(true)}>
                  <Plus size={12} /> Crear nueva ruta
                </button>
              </>
            ) : (
              <>
                <input type="text"
                  placeholder="NOMBRE DE LA NUEVA RUTA"
                  value={nuevaRuta}
                  onChange={e => setNuevaRuta(e.target.value)} />
                <div className="pc-color-row">
                  <label>Color:</label>
                  <input type="color" value={colorNueva}
                    onChange={e => setColorNueva(e.target.value)}
                    className="pc-color-input" />
                </div>
                <button type="button" className="pc-btn-link"
                  onClick={() => setCreandoNueva(false)}>
                  ← Volver a elegir ruta existente
                </button>
              </>
            )}
          </div>

          <div className="pc-modal-actions">
            <button type="button" onClick={onClose}>Cancelar</button>
            <button type="button" className="primary" onClick={handleMover}
              disabled={creandoNueva ? !nuevaRuta.trim() : !rutaDestino}>
              <ArrowRightLeft size={14} /> Mover
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ============================================================
   TABLA RUTAS — con drag & drop para reordenar ciudades
   ============================================================ */
const TablaRutas = ({ rutas, setRutas, ciudades, onNotify, onConfirm, onRefreshRutas }) => {
  const [editandoNombre, setEditandoNombre] = useState({});
  const [editandoColor, setEditandoColor] = useState({});
  const [guardando, setGuardando] = useState({});
  const [expandida, setExpandida] = useState(new Set());
  const [ciudadesSinRuta, setCiudadesSinRuta] = useState([]);
  const [loadingSinRuta, setLoadingSinRuta] = useState(true);
  const [modalMover, setModalMover] = useState(null);
  const [generando, setGenerando] = useState(false);
  const [filtro, setFiltro] = useState('');

  const [draggedInfo, setDraggedInfo] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  const cargarSinRuta = useCallback(async () => {
    setLoadingSinRuta(true);
    try {
      const res = await apiClient.get('/almacen/rutas/sin-asignar');
      if (res.data?.success) setCiudadesSinRuta(res.data.data || []);
    } catch (err) {
      console.error('Error sin-ruta:', err);
    } finally {
      setLoadingSinRuta(false);
    }
  }, []);

  useEffect(() => { cargarSinRuta(); }, [cargarSinRuta, rutas]);

  const rutasFiltradas = useMemo(() => {
    const q = filtro.toLowerCase().trim();
    if (!q) return rutas;
    return rutas.filter(r =>
      String(r.ruta || '').toLowerCase().includes(q) ||
      (r.ciudades || []).some(c => String(c.ciudad).toLowerCase().includes(q))
    );
  }, [rutas, filtro]);

  const toggleExpandir = (ruta) => {
    setExpandida(prev => {
      const n = new Set(prev);
      if (n.has(ruta)) n.delete(ruta); else n.add(ruta);
      return n;
    });
  };

  const guardarConfig = async (rutaOriginal) => {
    const cambios = {};
    if (editandoNombre[rutaOriginal] !== undefined
      && editandoNombre[rutaOriginal].trim().toUpperCase() !== rutaOriginal) {
      cambios.nuevo_nombre = editandoNombre[rutaOriginal];
    }
    if (editandoColor[rutaOriginal] !== undefined) {
      cambios.color = editandoColor[rutaOriginal];
    }
    if (Object.keys(cambios).length === 0) {
      setEditandoNombre(prev => { const n = { ...prev }; delete n[rutaOriginal]; return n; });
      setEditandoColor(prev => { const n = { ...prev }; delete n[rutaOriginal]; return n; });
      return;
    }

    setGuardando(prev => ({ ...prev, [rutaOriginal]: true }));
    try {
      const res = await apiClient.put(`/almacen/rutas/config/${rutaOriginal}`, cambios);
      if (res.data?.success) {
        onNotify?.('success', res.data.detail || 'Ruta actualizada');
        await onRefreshRutas?.();
        setEditandoNombre(prev => { const n = { ...prev }; delete n[rutaOriginal]; return n; });
        setEditandoColor(prev => { const n = { ...prev }; delete n[rutaOriginal]; return n; });
      } else {
        onNotify?.('error', res.data?.detail || 'Error al guardar');
      }
    } catch (err) {
      onNotify?.('error', err.response?.data?.detail || 'Error al guardar');
    } finally {
      setGuardando(prev => ({ ...prev, [rutaOriginal]: false }));
    }
  };

  const descartarConfig = (ruta) => {
    setEditandoNombre(prev => { const n = { ...prev }; delete n[ruta]; return n; });
    setEditandoColor(prev => { const n = { ...prev }; delete n[ruta]; return n; });
  };

  const moverCiudades = async (rutaDestino, ciudadesMover, colorDestino, ordenDestino) => {
    try {
      const res = await apiClient.put('/almacen/rutas/mover', {
        ciudades: ciudadesMover,
        ruta_destino: rutaDestino,
        color_destino: colorDestino,
        ruta_orden_destino: ordenDestino,
      });
      if (res.data?.success) {
        onNotify?.('success', res.data.detail || 'Ciudades movidas');
        await onRefreshRutas?.();
        setModalMover(null);
      } else {
        onNotify?.('error', res.data?.detail || 'Error al mover');
      }
    } catch (err) {
      onNotify?.('error', err.response?.data?.detail || 'Error al mover');
    }
  };

  const reordenarRuta = async (ruta, ciudadesOrdenadas) => {
    try {
      const res = await apiClient.put(`/almacen/rutas/reordenar/${ruta}`, {
        ciudades: ciudadesOrdenadas,
      });
      if (res.data?.success) {
        await onRefreshRutas?.();
      } else {
        onNotify?.('error', res.data?.detail || 'Error al reordenar');
      }
    } catch (err) {
      onNotify?.('error', 'Error al reordenar');
    }
  };

  const moverOrden = async (ruta, ciudadesRuta, idx, dir) => {
    if (dir === -1 && idx === 0) return;
    if (dir === 1 && idx === ciudadesRuta.length - 1) return;
    const nuevas = [...ciudadesRuta];
    [nuevas[idx], nuevas[idx + dir]] = [nuevas[idx + dir], nuevas[idx]];
    await reordenarRuta(ruta, nuevas.map(c => c.ciudad));
  };

  const handleDragStart = (e, ruta, idx) => {
    setDraggedInfo({ ruta, idx });
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', String(idx)); } catch (_) { }
  };

  const handleDragOver = (e, ruta, idx) => {
    if (!draggedInfo || draggedInfo.ruta !== ruta) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIdx !== idx) setDragOverIdx(idx);
  };

  const handleDragLeave = () => {
    setDragOverIdx(null);
  };

  const handleDrop = async (e, ruta, ciudadesRuta, idx) => {
    e.preventDefault();
    e.stopPropagation();

    if (!draggedInfo || draggedInfo.ruta !== ruta) {
      setDraggedInfo(null);
      setDragOverIdx(null);
      return;
    }
    if (draggedInfo.idx === idx) {
      setDraggedInfo(null);
      setDragOverIdx(null);
      return;
    }

    const nuevas = [...ciudadesRuta];
    const [removed] = nuevas.splice(draggedInfo.idx, 1);
    nuevas.splice(idx, 0, removed);

    setDraggedInfo(null);
    setDragOverIdx(null);

    await reordenarRuta(ruta, nuevas.map(c => c.ciudad));
  };

  const handleDragEnd = () => {
    setDraggedInfo(null);
    setDragOverIdx(null);
  };

  const autoGenerar = async () => {
    setGenerando(true);
    try {
      const res = await apiClient.post('/almacen/rutas/auto-generar');
      if (res.data?.success) {
        onNotify?.('success', res.data.detail || 'Rutas auto-generadas');
        await onRefreshRutas?.();
      } else {
        onNotify?.('error', res.data?.detail || 'Error al auto-generar');
      }
    } catch (err) {
      onNotify?.('error', err.response?.data?.detail || 'Error al auto-generar');
    } finally {
      setGenerando(false);
    }
  };

  return (
    <div className="pc-table-card pc-rutas-card">
      <div className="pc-table-header">
        <div className="pc-table-title">
          <Route size={16} />
          <h3>Rutas de Despacho</h3>
          <span className="pc-count">{rutas.length}</span>
          {ciudadesSinRuta.length > 0 && (
            <span className="pc-stat-chip amber" title="Ciudades activas sin ruta asignada">
              <AlertCircle size={11} /> {ciudadesSinRuta.length} sin ruta
            </span>
          )}
        </div>
        <div className="pc-table-actions">
          <button className="pc-btn-sync" onClick={autoGenerar} disabled={generando}
            title="Auto-generar rutas desde regiones (solo ciudades sin asignar)">
            <Wand2 size={13} className={generando ? 'spin-anim' : ''} />
          </button>
        </div>
      </div>

      <div className="pc-search-bar">
        <Search size={13} />
        <input type="text" placeholder="Filtrar por ruta o ciudad…"
          value={filtro} onChange={e => setFiltro(e.target.value)} />
      </div>

      <div className="pc-table-scroll">
        <table className="pc-table pc-table-rutas">
          <thead>
            <tr>
              <th style={{ width: 36 }} />
              <th style={{ minWidth: 180 }}>Ruta</th>
              <th style={{ width: 60 }}>Color</th>
              <th style={{ width: 70, textAlign: 'center' }}>Ciudades</th>
              <th>Ciudades (orden de visita)</th>
              <th style={{ width: 110 }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {rutasFiltradas.length === 0 ? (
              <tr><td colSpan={6} className="pc-empty">
                {rutas.length === 0 ? 'Sin rutas creadas' : 'Sin coincidencias'}
              </td></tr>
            ) : rutasFiltradas.map(r => {
              const abierta = expandida.has(r.ruta);
              const cambiado = editandoNombre[r.ruta] !== undefined
                || editandoColor[r.ruta] !== undefined;
              const colorActual = editandoColor[r.ruta] ?? r.color;
              const nombreActual = editandoNombre[r.ruta] ?? r.ruta;

              return (
                <React.Fragment key={r.ruta}>
                  <tr className={cambiado ? 'has-changes' : ''}>
                    <td>
                      <button className="pc-btn-icon"
                        onClick={() => toggleExpandir(r.ruta)}
                        title={abierta ? 'Ocultar ciudades' : 'Ver ciudades'}>
                        {abierta ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                      </button>
                    </td>
                    <td>
                      <input className="pc-input"
                        value={nombreActual}
                        onChange={e => setEditandoNombre(prev => ({
                          ...prev, [r.ruta]: e.target.value
                        }))} />
                    </td>
                    <td>
                      <div className="pc-color-wrap">
                        <input
                          type="color"
                          className="pc-color-input"
                          value={colorActual}
                          onChange={e => setEditandoColor(prev => ({
                            ...prev, [r.ruta]: e.target.value
                          }))}
                        />
                      </div>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span className="pc-count">{r.total_ciudades}</span>
                    </td>
                    <td>
                      <div className="pc-ruta-ciudades-pills">
                        {r.ciudades.slice(0, 5).map(c => (
                          <span key={c.ciudad} className="pc-ruta-ciudad-pill"
                            style={{
                              background: `${colorActual}22`,
                              borderColor: `${colorActual}66`,
                              color: colorActual,
                            }}>
                            {c.ciudad}
                          </span>
                        ))}
                        {r.ciudades.length > 5 && (
                          <span className="pc-ruta-ciudad-mas">
                            +{r.ciudades.length - 5}
                          </span>
                        )}
                        {r.ciudades.length === 0 && (
                          <span className="pc-empty-inline">Sin ciudades</span>
                        )}
                      </div>
                    </td>
                    <td className="pc-actions-cell">
                      {guardando[r.ruta] ? (
                        <span className="pc-saving">Guardando…</span>
                      ) : cambiado ? (
                        <div className="pc-row-actions">
                          <button className="pc-btn-save-row" onClick={() => guardarConfig(r.ruta)}>
                            <Save size={13} /> Guardar
                          </button>
                          <button className="pc-btn-cancel-row" onClick={() => descartarConfig(r.ruta)}>
                            <X size={13} />
                          </button>
                        </div>
                      ) : (
                        <div className="pc-row-actions">
                          <button className="pc-btn-icon"
                            title="Mover todas las ciudades a otra ruta"
                            onClick={() => {
                              if (r.ciudades.length === 0) return;
                              setModalMover({
                                origen: r.ruta,
                                ciudades: r.ciudades.map(c => c.ciudad),
                              });
                            }}
                            disabled={r.ciudades.length === 0}>
                            <ArrowRightLeft size={13} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                  {abierta && (
                    <tr>
                      <td />
                      <td colSpan={5} className="pc-ruta-detalle-cell">
                        <div className="pc-ruta-detalle-hint">
                          <GripVertical size={11} />
                          <span>Arrastra las ciudades para reordenar la ruta · o usa las flechas</span>
                        </div>
                        <div className="pc-ruta-ciudades-detalle">
                          {r.ciudades.length === 0 ? (
                            <div className="pc-empty-inline">
                              Esta ruta no tiene ciudades. Usa el botón <ArrowRightLeft size={11} /> en otra ruta para moverle ciudades.
                            </div>
                          ) : r.ciudades.map((c, idx) => {
                            const isDragging = draggedInfo?.ruta === r.ruta && draggedInfo?.idx === idx;
                            const isOver = dragOverIdx === idx && draggedInfo?.ruta === r.ruta;
                            return (
                              <div
                                key={c.ciudad}
                                draggable
                                onDragStart={(e) => handleDragStart(e, r.ruta, idx)}
                                onDragOver={(e) => handleDragOver(e, r.ruta, idx)}
                                onDragLeave={handleDragLeave}
                                onDrop={(e) => handleDrop(e, r.ruta, r.ciudades, idx)}
                                onDragEnd={handleDragEnd}
                                className={[
                                  'pc-ruta-ciudad-row',
                                  isDragging ? 'dragging' : '',
                                  isOver ? 'drag-over' : '',
                                ].filter(Boolean).join(' ')}
                                title="Arrastra para reordenar"
                              >
                                <span className="drag-handle">
                                  <GripVertical size={13} />
                                </span>
                                <span className="pc-ruta-ciudad-orden">{idx + 1}</span>
                                <span className="pc-ruta-ciudad-nombre">{c.ciudad}</span>
                                <div className="pc-ruta-ciudad-acciones">
                                  <button className="pc-btn-icon"
                                    disabled={idx === 0}
                                    onClick={() => moverOrden(r.ruta, r.ciudades, idx, -1)}
                                    title="Subir">
                                    <ArrowUp size={11} />
                                  </button>
                                  <button className="pc-btn-icon"
                                    disabled={idx === r.ciudades.length - 1}
                                    onClick={() => moverOrden(r.ruta, r.ciudades, idx, 1)}
                                    title="Bajar">
                                    <ArrowDown size={11} />
                                  </button>
                                  <button className="pc-btn-icon"
                                    onClick={() => setModalMover({
                                      origen: r.ruta,
                                      ciudades: [c.ciudad],
                                    })}
                                    title="Mover a otra ruta">
                                    <ArrowRightLeft size={11} />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {!loadingSinRuta && ciudadesSinRuta.length > 0 && (
        <div className="pc-ruta-sin-asignar">
          <div className="pc-ruta-sin-asignar-header">
            <AlertCircle size={14} />
            <strong>{ciudadesSinRuta.length} ciudades sin ruta asignada</strong>
            <span className="pc-ruta-sin-asignar-hint">
              Asígnalas a una ruta para que entren en las propuestas automáticas.
            </span>
          </div>
          <div className="pc-ruta-sin-asignar-list">
            {ciudadesSinRuta.map(c => (
              <span key={c.ciudad} className="pc-ruta-sin-pill">
                {c.ciudad}
                <button
                  className="pc-ruta-sin-pill-btn"
                  onClick={() => setModalMover({
                    origen: null,
                    ciudades: [c.ciudad],
                  })}
                  title="Asignar a una ruta">
                  <Plus size={10} />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {modalMover && (
        <ModalMoverCiudades
          origen={modalMover.origen}
          ciudades={modalMover.ciudades}
          rutas={rutas}
          onClose={() => setModalMover(null)}
          onMover={(rutaDestino, color, orden) =>
            moverCiudades(rutaDestino, modalMover.ciudades, color, orden)
          }
        />
      )}
    </div>
  );
};


/* ============================================================
   PÁGINA: PANEL DE ALMACÉN (CONFIGURACIÓN LOGÍSTICA)
   ============================================================ */
const PanelAlmacen = () => {
  const navigate = useNavigate();
  const [camiones, setCamiones] = useState([]);
  const [ciudades, setCiudades] = useState([]);
  const [rutas, setRutas] = useState([]);
  const [loading, setLoading] = useState({
    camiones: true, ciudades: true, rutas: true,
  });
  const [error, setError] = useState('');

  const [showModalCamion, setShowModalCamion] = useState(false);
  const [showModalCiudad, setShowModalCiudad] = useState(false);

  const [toast, setToast] = useState({ show: false, type: 'info', message: '' });
  const notify = useCallback((type, message) => setToast({ show: true, type, message }), []);
  const hideToast = useCallback(() => setToast(prev => ({ ...prev, show: false })), []);

  const [confirmDialog, setConfirmDialog] = useState({
    show: false, title: '', message: '', onConfirm: null, loading: false,
  });
  const confirmar = useCallback((title, message, onConfirm) => {
    setConfirmDialog({ show: true, title, message, onConfirm, loading: false });
  }, []);
  const handleConfirm = useCallback(async () => {
    if (!confirmDialog.onConfirm) return;
    setConfirmDialog(prev => ({ ...prev, loading: true }));
    try { await confirmDialog.onConfirm(); }
    finally {
      setConfirmDialog({
        show: false, title: '', message: '', onConfirm: null, loading: false,
      });
    }
  }, [confirmDialog]);

  const fetchCamiones = useCallback(async () => {
    setLoading(prev => ({ ...prev, camiones: true }));
    try {
      const res = await apiClient.get('/almacen/camiones');
      setCamiones(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      console.error('Error camiones:', err);
    } finally {
      setLoading(prev => ({ ...prev, camiones: false }));
    }
  }, []);

  const fetchCiudades = useCallback(async () => {
    setLoading(prev => ({ ...prev, ciudades: true }));
    try {
      const res = await apiClient.get('/almacen/ciudades');
      setCiudades(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      console.error('Error ciudades:', err);
    } finally {
      setLoading(prev => ({ ...prev, ciudades: false }));
    }
  }, []);

  const fetchRutas = useCallback(async () => {
    setLoading(prev => ({ ...prev, rutas: true }));
    try {
      const res = await apiClient.get('/almacen/rutas');
      if (res.data?.success) {
        setRutas(Array.isArray(res.data.data) ? res.data.data : []);
      }
    } catch (err) {
      console.error('Error rutas:', err);
    } finally {
      setLoading(prev => ({ ...prev, rutas: false }));
    }
  }, []);

  useEffect(() => {
    fetchCamiones();
    fetchCiudades();
    fetchRutas();
  }, [fetchCamiones, fetchCiudades, fetchRutas]);

  const refreshAll = () => {
    fetchCamiones();
    fetchCiudades();
    fetchRutas();
  };

  const isLoadingAny = loading.camiones || loading.ciudades || loading.rutas;

  return (
    <div className="dashboard-container">
      <DashboardMenuHeader />
      <header className="header-flex">
        <div className="title-section">
          <h1 className="dashboard-title">Panel de Almacén</h1>
          <div className="title-underline" />
          <p className="dashboard-subtitle" style={{ margin: '4px 0 0 0', fontSize: '0.82rem', color: '#94a3b8' }}>
            Parametrización Logística: Flota de Camiones, Tarifario de Ciudades y Rutas de Despacho
          </p>
        </div>
        <div className="date-selectors" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            className="pc-nav-link-btn accent"
            onClick={() => navigate('/consolidaciondespacho')}
            title="Ir a la Consolidación Automática de Despachos">
            <Truck size={15} /> Consolidación de Despacho
          </button>
          <button className="refresh-button" onClick={refreshAll} disabled={isLoadingAny} title="Refrescar datos">
            <RefreshCw size={15} className={isLoadingAny ? 'spin-anim' : ''} />
          </button>
        </div>
      </header>

      {error && (
        <div className="pc-error-banner">
          <AlertCircle size={14} />
          <span>{error}</span>
          <button onClick={() => setError('')}><X size={14} /></button>
        </div>
      )}

      {/* Tablas de Configuración */}
      <div className="pc-config-tables">
        <TablaCamiones
          camiones={camiones}
          setCamiones={setCamiones}
          onNuevo={() => setShowModalCamion(true)}
          onNotify={notify}
          onConfirm={confirmar}
        />
        <TablaCiudades
          ciudades={ciudades}
          setCiudades={setCiudades}
          onNuevo={() => setShowModalCiudad(true)}
          onSync={fetchCiudades}
          onNotify={notify}
          onConfirm={confirmar}
        />
      </div>

      <TablaRutas
        rutas={rutas}
        setRutas={setRutas}
        ciudades={ciudades}
        onNotify={notify}
        onConfirm={confirmar}
        onRefreshRutas={fetchRutas}
      />

      {showModalCamion && (
        <ModalNuevoCamion
          onClose={() => setShowModalCamion(false)}
          onCreated={n => setCamiones(prev => [...prev, n])}
          onNotify={notify}
        />
      )}

      {showModalCiudad && (
        <ModalNuevaCiudad
          onClose={() => setShowModalCiudad(false)}
          onCreated={n => setCiudades(prev => [...prev, n])}
          onNotify={notify}
        />
      )}

      <Toast show={toast.show} type={toast.type} message={toast.message} onClose={hideToast} />

      <ConfirmDialog
        show={confirmDialog.show}
        title={confirmDialog.title}
        message={confirmDialog.message}
        loading={confirmDialog.loading}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmDialog({
          show: false, title: '', message: '', onConfirm: null, loading: false,
        })}
      />
    </div>
  );
};

export default PanelAlmacen;
